/**
 * Where the API lives, what analytics it reports to, and which extra search
 * params this site declared.
 *
 * `wp_add_inline_script( 'ionata-ai-search-store', ..., 'before' )` is the
 * PHP-to-JS channel, printing each global immediately above the script tag
 * (`src/Components.php`). So this reads at module scope rather than lazily: by
 * the time anything here evaluates, the globals are either set or the page did
 * not come from this plugin, and neither case gets better by waiting.
 *
 * Reading them once also makes the missing-baseUrl error a single line in the
 * console at load rather than one per search, and makes the param declaration
 * available before Alpine walks the DOM.
 */

/**
 * The shape the host page sets.
 *
 * Every value optional. `wp_json_encode` preserves what PHP had, so an unset
 * setting arrives as `null` and a numeric one arrives as a number.
 */
type AISearchConfig = {
  /**
   * Origin of the nitro server, e.g. `https://search.example.com`.
   *
   * Required. The plugin serves this bundle from the WordPress site, not from
   * the API host, so there is no origin to infer one from.
   */
  baseUrl?: string | null;
  /** Enables PostHog capture and supplies the `x-distinct-id` sent to the API. */
  posthogApiKey?: string | null;
  /** PostHog ingestion host, default `https://eu.i.posthog.com`. */
  posthogApiHost?: string | null;
};

/** How a declared param is spelled in the URL: once, or repeated. */
export type ParamArity = "one" | "many";

function ambientConfig(): AISearchConfig {
  const value = (globalThis as Record<string, unknown>).AISearchConfig;
  // `typeof [] === "object"`, and a filter returning `array_values(...)` by
  // accident lands here as a JS array. It is not a config object.
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as AISearchConfig)
    : {};
}

/**
 * There is no default.
 *
 * Falling back to the origin the bundle was served from would point every
 * request at the WordPress site, which serves the script but does not answer
 * the API, and 404 quietly on each one. An empty `baseUrl` is a
 * misconfiguration, and saying so beats guessing.
 *
 * The trailing slash goes because every caller concatenates a rooted path onto
 * this, and `https://host//api/search` is not a URL anyone wants to debug.
 */
function resolveBaseUrl(explicit?: string | null): string {
  if (explicit) {
    return explicit.replace(/\/+$/, "");
  }
  console.error(
    "[ai-search] no baseUrl configured, so API requests will go to this site " +
      "and fail. Set 'AI Server URL' under ElasticPress > Features > Ionata AI Search.",
  );
  return "";
}

const config = ambientConfig();

export const baseUrl = resolveBaseUrl(config.baseUrl);

/* A WordPress setting stores `''` for a text field nobody filled in, and `null`
   when it is unset. Folding both to `undefined` is what lets the rest of the
   bundle tell "not configured" from "configured to nothing". */

export const posthogApiKey = config.posthogApiKey || undefined;
export const posthogApiHost = config.posthogApiHost || undefined;

/**
 * The declared search params, by name.
 *
 * Read at import from its own global, which `wp_add_inline_script` prints above
 * this bundle's tag. That timing is the whole reason the declaration lives in
 * PHP. Alpine calls a store's `init()` from inside its `alpine:init` dispatch,
 * before the initial DOM walk, and the URL is read in that `init()`, so a list
 * sniffed from the markup would not exist yet.
 *
 * Empty is the default and means the site has not asked for extra params.
 */
export const declaredParams: ReadonlyMap<string, ParamArity> =
  readDeclaredParams();

/**
 * How a declared param is spelled in a URL. `Params::url_key()` is the PHP
 * mirror.
 *
 * A repeatable param carries PHP's `[]`, because PHP is at both ends of this,
 * rendering the classic form and serving the REST endpoint, and `?topic=a&topic=b`
 * reads back there as the string `"b"`. Single-valued params are unchanged.
 *
 * The store's own key is always the bare name; this is the URL spelling only.
 */
export function urlKey(name: string): string {
  return declaredParams.get(name) === "many" ? `${name}[]` : name;
}

/**
 * The declaration as PHP prints it: `{"post_type":"one","department":"many"}`.
 *
 * An array is rejected rather than interpreted. PHP's empty array is ambiguous
 * between a list and a map, so `Components::register_assets()` casts to an
 * object to force `{}`. An array arriving here is therefore a filter that
 * returned the wrong shape, and guessing at it would turn a loud mistake into a
 * search that quietly ignores its filters.
 */
function readDeclaredParams(): Map<string, ParamArity> {
  const raw = (globalThis as Record<string, unknown>).AISearchParams;
  const out = new Map<string, ParamArity>();

  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;

  for (const [name, arity] of Object.entries(raw)) {
    // PHP has already dropped the reserved names and the malformed entries, so
    // nothing here has to know what they are. This is the guard for a page that
    // set the global itself.
    if (!name) continue;
    if (arity !== "one" && arity !== "many") continue;
    out.set(name, arity);
  }

  return out;
}
